import { useCallback } from 'react';
import { useCreatePackageBlockMutation } from '../../../../api/mutations/createPackageBlock';
import { useUpdatePackageBlockMutation } from '../../../../api/mutations/updatePackageBlock';
import { BlockType } from '../../../../api/types';
import useHandlePackageSetting from './useHandlePackageSetting';
import { type PackageBlockFormValues, type PackageBlockInput } from './utils';

const useHandlePackageBlock = () => {
    const [createPackageBlockMutation] = useCreatePackageBlockMutation();
    const [updatePackageBlockMutation] = useUpdatePackageBlockMutation();

    const { createPackageSetting, updatePackageSetting, deletePackageSetting } = useHandlePackageSetting();

    // Create package block execution
    const createPackageBlock = useCallback(
        async (variantConfiguratorId: string, blockInput: PackageBlockInput) => {
            const { packageSettings, isActive } = blockInput;

            const { data } = await createPackageBlockMutation({
                variables: {
                    variantConfiguratorId,
                    settings: {
                        type: BlockType.Package,
                        packageBlockInput: {
                            isActive,
                        },
                    },
                },
            });

            const packageBlock = data?.packageBlock;

            const packageSettingPromises = packageSettings.map(packageSetting =>
                createPackageSetting(variantConfiguratorId, packageBlock.id, packageSetting)
            );

            if (packageSettingPromises.length > 0) {
                await Promise.all(packageSettingPromises);
            }
        },
        [createPackageBlockMutation, createPackageSetting]
    );

    // Update package block execution
    const updatePackageBlock = useCallback(
        async (variantConfiguratorId: string, blockId: string, settings: PackageBlockInput) => {
            const { isActive, packageSettings } = settings;

            const { data } = await updatePackageBlockMutation({
                variables: {
                    variantConfiguratorId,
                    blockId,
                    settings: {
                        type: BlockType.Package,
                        packageBlockInput: {
                            isActive,
                        },
                    },
                },
            });

            const packageBlock = data?.variantConfigurator.packageBlock;

            // deleting package setting
            const packageSettingDeletePromises = packageBlock.packageSettings.map(initialSetting => {
                const packageSettingToDelete = packageSettings.find(
                    packageSettingToFind => packageSettingToFind.id === initialSetting.id
                );

                if (!packageSettingToDelete) {
                    return deletePackageSetting(initialSetting, blockId, variantConfiguratorId);
                }

                return Promise.resolve();
            });

            if (packageSettingDeletePromises.length > 0) {
                await Promise.all(packageSettingDeletePromises);
            }

            // create or update package setting
            const packageSettingPromises = packageSettings.map(packageSetting => {
                if (packageSetting.id) {
                    return updatePackageSetting(packageSetting, blockId, variantConfiguratorId);
                }

                return createPackageSetting(variantConfiguratorId, blockId, packageSetting);
            });

            if (packageSettingPromises.length > 0) {
                await Promise.all(packageSettingPromises);
            }
        },
        [createPackageSetting, deletePackageSetting, updatePackageBlockMutation, updatePackageSetting]
    );

    return useCallback(
        async (values: PackageBlockFormValues, variantConfiguratorId: string) => {
            const { packageBlockInput } = values;
            const { id } = packageBlockInput;

            if (id) {
                return updatePackageBlock(variantConfiguratorId, id, packageBlockInput);
            }

            return createPackageBlock(variantConfiguratorId, packageBlockInput);
        },
        [createPackageBlock, updatePackageBlock]
    );
};

export default useHandlePackageBlock;
